import React from 'react';
import { Provider } from 'react-redux';
import store from './babel/store';
import Routes from 'App/pages/routes';
import AngularApp from './AngularApp';
import Modals from './components/Modals';
import AppOnboarding from 'App/pages/AppOnboarding'; // Rename if not intended to replace current onboarding
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import SystemNotifications from 'Components/SystemNotifications/SystemNotifications';
import { Route, Router } from 'react-router-dom';
import history from 'App/pages/routes/history';
import Login from 'App/pages/Login';
import MCPLogin from 'App/pages/MCPLogin/MCPLogin';
import EmbedAIChat from 'App/pages/EmbedAIChat';
import PanelRedirect from 'App/pages/EmbedAIChat/PanelRedirect';
import FloatingAIChat from 'App/pages/FloatingAIChat/FloatingAIChat';
import { useGlobalChatShortcut } from 'App/pages/FloatingAIChat/useGlobalChatShortcut';
import { useFloatingChatAccess } from 'App/pages/FloatingAIChat/useFloatingChatAccess';
import { doStaticSetup } from './helpers/appHelper';
import Session, { onlyRenderWhenAuthenticated } from './Session';
import NavbarComponent from './navbar/Navbar';
import AppSidebarComponent from 'App/components/AppSidebar';
doStaticSetup();

const Navbar = onlyRenderWhenAuthenticated(NavbarComponent);
const AppSidebar = onlyRenderWhenAuthenticated(AppSidebarComponent);

// Gate the Cmd/Ctrl+U shortcut on the same access as the window — otherwise it would
// preventDefault() (suppressing the browser's View Source) for every authenticated user,
// including those without the feature, while opening nothing.
const ChatShortcut = onlyRenderWhenAuthenticated(() => {
	useGlobalChatShortcut(useFloatingChatAccess());
	return null;
});

// Generate a deterministic color from the branch name so all services (UI, master-ui)
// on the same worktree get the same color.
const getWorktreeBorderColor = () => {
	const key = process.env.WORKTREE_BRANCH || process.env.WORKTREE_PORT;
	if (!key) return null;
	const colors = ['#e74c3c', '#2ecc71', '#3498db', '#9b59b6', '#e67e22', '#1abc9c', '#f39c12', '#e91e63'];
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = key.charCodeAt(i) + ((hash << 5) - hash);
	}
	return colors[Math.abs(hash) % colors.length];
};
const worktreeBorderColor = getWorktreeBorderColor();
const worktreeBranch = process.env.WORKTREE_BRANCH || '';

const App = () => {
	const content = (
		// The shared hash history, NOT a <HashRouter> of our own: that would build a second
		// createHashHistory() alongside the singleton the rest of the app pushes to (Tools.routerHistory,
		// ReduxComponentRoot, ~50 modules). Two hash histories only hear about each other's navigations
		// from the hashchange event, as asynchronous POPs — so they agree on the URL but nothing can
		// intercept a navigation synchronously, which is what broke the unsaved-changes guard (WEB-9102).
		<Router history={history}>
			<Provider store={store}>
				{/* Make Session component handle init and dispatch actions */}
				<Session>
					<Route path="/login" component={Login} />
					<Route path="/mcp-login" component={MCPLogin} />
					<Route path="/embed/ai-chat" component={EmbedAIChat} />
					<PanelRedirect />
					<AngularApp>
						<DndProvider backend={HTML5Backend}>
							<div>
								<Navbar />
								<AppSidebar />
								<Routes />
								<Modals />
								<AppOnboarding />
								<FloatingAIChat />
								<ChatShortcut />
							</div>
							<SystemNotifications />
						</DndProvider>
					</AngularApp>
				</Session>
			</Provider>
		</Router>
	);

	if (!worktreeBorderColor) return content;

	return (
		<>
			{worktreeBranch && (
				<div
					style={{
						position: 'fixed',
						bottom: 8,
						right: 8,
						padding: '4px 10px',
						borderRadius: 4,
						background: worktreeBorderColor,
						color: '#fff',
						fontWeight: 700,
						fontSize: 12,
						fontFamily: 'monospace',
						zIndex: 99999,
						pointerEvents: 'none',
						opacity: 0.9
					}}
				>
					{worktreeBranch}
				</div>
			)}
			{content}
			<div
				style={{
					position: 'fixed',
					inset: 0,
					border: `4px solid ${worktreeBorderColor}`,
					pointerEvents: 'none',
					zIndex: 99999
				}}
			/>
		</>
	);
};

export default App;
